import { useProgressApiClient } from "@api/progress-api/use-progress-api-client";
import { getProjectApiClient } from "@api/project-api/project-api-utils";
import WarningIcon from "@assets/icons/new/warning_60px.svg?react";
import { EmptyPage } from "@components/common/empty-page/empty-page";
import { FaroBetaBadge } from "@components/common/faro-beta-badge";
import { SphereButton } from "@components/common/sphere-button";
import { TabsWithRoutes } from "@components/common/tabs/tabs-with-routes";
import { SdbBackgroundTask } from "@custom-types/sdb-background-tasks-types";
import { ContactSupportLocations } from "@custom-types/types";
import { clearStore } from "@faro-lotv/project-source";
import { useHasUserValidFeatureCompanyLevel } from "@hooks/feature-control/use-has-user-valid-feature-company-level";
import { useHasUserValidPermissionProjectLevel } from "@hooks/permission-control/use-has-user-valid-permission-project-level";
import { Stack, Typography } from "@mui/material";
import { NotFoundPage } from "@pages/not-found-page";
import { ProjectBackgroundTasks } from "@pages/project-details/project-bg-tasks/project-background-tasks";
import { ProjectBackgroundTasksSkeleton } from "@pages/project-details/project-bg-tasks/project-background-tasks-skeleton";
import { ProjectButtons } from "@pages/project-details/project-buttons";
import { ProjectData } from "@pages/project-details/project-data/project-data";
import { ProjectIntegrations } from "@pages/project-details/project-integrations/project-integrations";
import { ProjectMarkups } from "@pages/project-details/project-markups/project-markups";
import { ProjectOverview } from "@pages/project-details/project-overview/project-overview";
import { ProjectSettings } from "@pages/project-details/project-settings/project-settings";
import { ProjectTeam } from "@pages/project-details/project-team/project-team";
import { ProjectTabs } from "@router/route-params";
import { useAppParams } from "@router/router-helper";
import { appStateSelector } from "@store/app/app-selector";
import {
  deletedProjectsSelector,
  fetchingProjectsFlagsSelector,
  isProjectArchiveSelector,
  selectedProjectContextSelector,
  selectedProjectSelector,
  shouldDisplayMarkupsForViewersSelector,
} from "@store/projects/projects-selector";
import {
  resetDeletedProjects,
  resetSelectedProject,
} from "@store/projects/projects-slice";
import {
  fetchProjectContext,
  fetchProjectDetails,
  fetchProjectSettings,
} from "@store/projects/projects-slice-thunk";
import {
  backgroundTasksFetchingStatusSelector,
  fetchingStatusByTypeSelector,
  sdbBackgroundTasksSelector,
} from "@store/sdb-background-tasks/sdb-background-tasks-selector";
import { resetSdbBackgroundTasksState } from "@store/sdb-background-tasks/sdb-background-tasks-slice";
import { fetchTasksByType } from "@store/sdb-background-tasks/sdb-background-tasks-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { contactSupport } from "@utils/email-utils";
import { RequiredPermissionProjectLevelName } from "@utils/permission-control/project-permission-control-types";
import { OpenTabEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useEffect, useState } from "react";

/**
 * Filters the given tasks to only include VideoMode tasks unless dev mode is enabled
 */
export function filterVideoModeTasks(
  tasks: SdbBackgroundTask[],
  isDevModeEnabled: boolean
): SdbBackgroundTask[] {
  return tasks.filter(
    (task) => isDevModeEnabled || task.taskType === "VideoMode"
  );
}

/**
 * Display the details of the selected project
 */
export function ProjectDetails(): JSX.Element | null {
  const { companyId, projectId, projectTabs } = useAppParams();
  const { trackEvent } = useTrackEvent();
  const dispatch = useAppDispatch();
  const selectedProject = useAppSelector(selectedProjectSelector);
  const isProjectArchived = useAppSelector(
    isProjectArchiveSelector(projectId ?? "")
  );
  const {
    isFetchingProjects,
    isFetchingProjectContext,
    isFetchingProjectSettings,
    processingProjects,
  } = useAppSelector(fetchingProjectsFlagsSelector);
  const deletedProjects = useAppSelector(deletedProjectsSelector);
  const selectedProjectContext = useAppSelector(selectedProjectContextSelector);
  const { isBetaTestingEnabled, isDevModeEnabled } =
    useAppSelector(appStateSelector);
  const shouldDisplayMarkupsForViewers = useAppSelector(
    shouldDisplayMarkupsForViewersSelector
  );
  const progressApiClient = useProgressApiClient({
    projectId: projectId ?? "",
  });

  const sdbBackgroundTasks = useAppSelector(sdbBackgroundTasksSelector);

  /** Whether the background tasks have been loaded at least once */
  const [hasLoadedTasksOnce, setHasLoadedTasksOnce] = useState(false);

  /**
   * Use the VideoMode-specific fetching status to avoid race conditions
   * in case ALL background tasks are being fetched at the same time
   */
  const videoModeTasksFetchingStatus = useAppSelector(
    fetchingStatusByTypeSelector("VideoMode")
  );
  /**
   * In dev mode, we show ALL task types (not just VideoMode), so we need to
   * check the general fetching status which is updated by useDataManagement
   */
  const allTasksFetchingStatus = useAppSelector(
    backgroundTasksFetchingStatusSelector
  );

  const { canViewCompanyMembers } = useHasUserValidFeatureCompanyLevel();
  const { canViewAllProjectMembers } = useHasUserValidPermissionProjectLevel();

  /** Whether the required data to display the project details page is loading */
  const isLoading =
    isFetchingProjects || isFetchingProjectContext || isFetchingProjectSettings;

  /**
   * Fetches the required data to display the project details:
   * - project details
   * - project context
   * - project settings
   * - background tasks
   */
  useEffect(() => {
    if (projectId) {
      const projectApiClient = getProjectApiClient({
        projectId,
      });

      // We need to check the project settings for viewers as well
      // to check if the annotation visibility is enabled for viewers or not
      void dispatch(fetchProjectSettings({ projectApiClient }));

      void dispatch(fetchProjectContext({ projectId }));

      if (companyId) {
        void dispatch(fetchProjectDetails({ companyId, projectId }));
      }

      void dispatch(
        fetchTasksByType({ progressApiClient, taskType: "VideoMode" })
      );
    }
  }, [companyId, dispatch, progressApiClient, projectId]);

  // Resets all store state associated to the selected project:
  // - Selected project from the projects slice.
  // - IElements slice since it should only store data of the selected project
  // - Progress API tasks slice since it should only store data of the selected project.
  useEffect(() => {
    return () => {
      dispatch(resetSelectedProject());
      dispatch(clearStore());
      dispatch(resetSdbBackgroundTasksState());
      dispatch(resetDeletedProjects());
    };
  }, [dispatch]);

  /** Filter out non-video mode tasks unless dev mode is enabled */
  const filteredSdbBackgroundTasks = filterVideoModeTasks(
    sdbBackgroundTasks,
    isDevModeEnabled
  );

  /**
   * We consider the fetch "completed" when it either succeeded or failed.
   * In dev mode, we show ALL task types (via filterVideoModeTasks), so we check
   * the general fetching status. In normal mode, we use the VideoMode-specific
   * fetching status to avoid race conditions with useDataManagement.
   */
  const relevantFetchingStatus = isDevModeEnabled
    ? allTasksFetchingStatus
    : videoModeTasksFetchingStatus;
  const isBackgroundTasksFetchCompleted =
    relevantFetchingStatus !== "uninitialized" &&
    relevantFetchingStatus !== "pending";

  /**
   * The Activity tab should be hidden when there are no background tasks.
   * We use hasLoadedTasksOnce to prevent "Access denied" errors when users reload
   * the page with the Activity tab open - it shows loading content until
   * the fetch completes.
   */
  const shouldHideActivityTab =
    isBackgroundTasksFetchCompleted && filteredSdbBackgroundTasks.length === 0;

  // useEffect to set hasLoadedTasksOnce to true once the background tasks have been fetched at least once
  useEffect(() => {
    if (isBackgroundTasksFetchCompleted) {
      setHasLoadedTasksOnce(true);
    }
  }, [isBackgroundTasksFetchCompleted]);

  /**
   * Early exit to the "Not found" page if the requires URL route params are not defined or their value is not valid:
   * - companyId
   * - projectId
   * - projectTabs
   */
  if (
    !companyId ||
    !projectId ||
    !projectTabs ||
    !Object.values(ProjectTabs).includes(projectTabs)
  ) {
    return <NotFoundPage />;
  }

  if (projectTabs === ProjectTabs.evaluation) {
    return (
      <EmptyPage
        title="The feature is no longer supported"
        subtitle="The feature you are looking for is no longer supported in Sphere XG"
        icon={WarningIcon}
        button={
          <SphereButton
            onClick={() =>
              contactSupport({
                location:
                  ContactSupportLocations.projectEvaluationDeprecatedPage,
                trackEvent,
              })
            }
          >
            Contact support
          </SphereButton>
        }
      />
    );
  }

  /** Whether the current project is being processed or has been recently deleted */
  const isProcessingOrDeleting =
    projectId && (processingProjects[projectId] || deletedProjects[projectId]);

  /**
   * Early exit to the "Not found" page if after fetching the required data to show the project details
   * one data element is not defined:
   * - project details
   * - project context
   * - project settings
   */
  if (
    (selectedProject === null || selectedProjectContext === null) &&
    !isLoading &&
    !isProcessingOrDeleting
  ) {
    return <NotFoundPage />;
  }

  /** Shows action buttons for the project, including Open Project, Data Management, and other options */
  const projectButtons = selectedProject ? (
    <ProjectButtons project={selectedProject} isLoading={isLoading} />
  ) : undefined;

  /**
   * Hide the members tab if
   * - selected project is not yet available to check access permission.
   * - the user has no workspace or project permission to view project members
   */
  const shouldHideMembersTab =
    !selectedProject || !(canViewAllProjectMembers || canViewCompanyMembers);

  return (
    <TabsWithRoutes
      selectedTab={projectTabs}
      selectedProject={selectedProject}
      requiredAttribute={selectedProject}
      trackingEventPageName={OpenTabEvents.openProjectTab}
      isLoading={isLoading}
      tabs={[
        {
          label: "Overview",
          route: ProjectTabs.overview,
          content: (project) => (
            <ProjectOverview project={project} isLoading={isLoading} />
          ),
          loadingContent: <ProjectOverview isLoading={true} />,
          actionButtons: projectButtons,
        },
        {
          label: "Members",
          route: ProjectTabs.members,
          content: (project) => (
            <ProjectTeam
              project={project}
              companyId={companyId}
              isLoading={isLoading}
            />
          ),
          loadingContent: <ProjectTeam isLoading={true} />,
          actionButtons: projectButtons,
          isHidden: shouldHideMembersTab,
        },
        {
          label: "Data",
          route: ProjectTabs.data,
          content: (project) => <ProjectData projectId={project.id} />,
          loadingContent: null,
          actionButtons: projectButtons,
          requiredPermissionProjectLevel:
            RequiredPermissionProjectLevelName.canViewProjectData,
          isHidden: !isBetaTestingEnabled,
        },
        {
          label: "Activity",
          route: ProjectTabs.activity,
          content: (project) => (
            <ProjectBackgroundTasks projectId={project.id} />
          ),
          loadingContent: <ProjectBackgroundTasksSkeleton />,
          actionButtons: projectButtons,
          requiredPermissionProjectLevel: isDevModeEnabled
            ? undefined
            : RequiredPermissionProjectLevelName.canViewProjectCloudActivity,
          isHidden: shouldHideActivityTab,
          isTabLoading: !hasLoadedTasksOnce,
        },
        {
          label: "Annotations",
          route: ProjectTabs.annotations,
          content: (project) => <ProjectMarkups projectId={project.id} />,
          loadingContent: <div />,
          actionButtons: projectButtons,
          requiredPermissionProjectLevel: shouldDisplayMarkupsForViewers
            ? RequiredPermissionProjectLevelName.canViewProjectMarkupsAllowViewers
            : RequiredPermissionProjectLevelName.canViewProjectMarkups,
        },
        {
          label: "Integrations",
          route: ProjectTabs.integrations,
          content: (project) => <ProjectIntegrations projectId={project.id} />,
          loadingContent: <div />,
          actionButtons: projectButtons,
          requiredPermissionProjectLevel:
            RequiredPermissionProjectLevelName.canViewProjectIntegrations,
          isHidden: isProjectArchived,
          isBetaFeature: true,
          popover: {
            title: (
              <Stack flexDirection={"row"} gap={"8px"}>
                Integrations
                <FaroBetaBadge />
              </Stack>
            ),
            description: (
              <Typography fontSize={"12px"} fontWeight={"400"}>
                Connect Sphere XG with third-party integrations to sync your
                annotations from Sphere XG to external tools. This feature is in
                beta and may evolve based on your feedback.
              </Typography>
            ),
            shouldShowFeedbackButton: false,
          },
        },
        {
          label: "Settings",
          route: ProjectTabs.settings,
          content: (project) => (
            <ProjectSettings projectId={project.id} isLoading={isLoading} />
          ),
          loadingContent: <ProjectSettings isLoading={true} />,
          actionButtons: projectButtons,
          requiredPermissionProjectLevel:
            RequiredPermissionProjectLevelName.canViewProjectSettings,
        },
      ]}
    />
  );
}
